import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './CouponRemovePopup.scss';
import { useSelector } from 'react-redux';

interface CouponRemovePopupProps {
  open: boolean;
  onKeep: () => void;
  onRemove: () => void;
  isMobile?: boolean;
  title?: string;
  message?: string;
  keepLabel?: string;
  removeLabel?: string;
}

// Critical styles inlined so the popup renders correctly even when its
// scss bundle hasn't been picked up by the dev server yet. The .scss file
// still ships matching class hooks for refinement (hover states, etc.).
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  zIndex: 9999,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 10,
};
const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  color: '#1A1A1A',
  flex: 1,
};
const closeStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: 'none',
  background: '#F2F2F2',
  color: '#555555',
  fontSize: 18,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
};
const btnBase: React.CSSProperties = {
  flex: 1,
  padding: '12px 16px',
  borderRadius: 999,
  fontFamily: "'Outfit', sans-serif",
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid var(--primary-color)',
};
const keepStyle: React.CSSProperties = {
  ...btnBase,
  background: '#FFFFFF',
  color: 'var(--primary-color)',
};
const removeStyle: React.CSSProperties = {
  ...btnBase,
  background: 'var(--primary-color)',
  color: '#FFFFFF',
};

const CouponRemovePopup: React.FC<CouponRemovePopupProps> = ({
  open,
  onKeep,
  onRemove,
  isMobile,
  title = 'Remove Offer?',
  message = 'Are you sure you want to remove this offer from your order?',
  keepLabel = 'Keep',
  removeLabel = 'Remove',
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Stop native `mousedown` from bubbling past the popup overlay.
  // The cart panel's outside-click handler is attached to `document` with
  // `addEventListener('mousedown', ...)`. It fires on the bubble phase
  // BEFORE React's synthetic `onClick` runs, which would unmount mobileCart
  // (and the portaled popup with it) — meaning the button click never
  // makes it to `onRemove`. Catching mousedown natively on the overlay and
  // calling stopPropagation prevents the document listener from firing, so
  // the cart stays mounted and React's onClick on Remove/Keep can complete.
  useEffect(() => {
    if (!open) return;
    const overlay = overlayRef.current;
    if (!overlay) return;
    const stop = (e: Event) => e.stopPropagation();
    overlay.addEventListener('mousedown', stop);
    return () => overlay.removeEventListener('mousedown', stop);
  }, [open]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const dialogStyle: React.CSSProperties = {
  width: '100%',
  maxWidth:isMobile ? 340 : 450,
  background: '#FFFFFF',
  borderRadius: 16,
  padding: 20,
 left: isMobile?'-1rem':"0rem",
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
  fontFamily: "'Outfit', sans-serif",
  position: 'relative',
  boxSizing: 'border-box',
};

const messageStyle: React.CSSProperties = {
  margin: '0 0 20px 0',
  marginBottom: isMobile ? 20 : 30,
  fontSize: 14,
  fontWeight: 400,
  color: '#555555',
  lineHeight: 1.4,
};

  const content = (
    <div
      ref={overlayRef}
      className="couponRemovePopup-overlay"
      style={overlayStyle}
      onClick={onKeep}
    >
      <div
        className="couponRemovePopup-dialog"
        style={dialogStyle}
        onClick={e => e.stopPropagation()}
      >
        <div className="couponRemovePopup-header">
          <p className="couponRemovePopup-title" >{title}</p>
        
           
              <button className={`Inavlid-coupon-close-btn ${isMobile?"remove-itemlist-close":""}`}    onClick={onKeep}>
          ✕
        </button>
         
        </div>
        <p className="couponRemovePopup-message" style={messageStyle}>{message}</p>
        <div className="couponRemovePopup-actions" style={actionsStyle}>
          <button
            type="button"
            className="couponRemovePopup-btn couponRemovePopup-btn--keep"
            // style={keepStyle}
            onClick={onKeep}
          >
            {keepLabel}
          </button>
          <button
            type="button"
            className="couponRemovePopup-btn couponRemovePopup-btn--remove"
            // style={removeStyle}
            onClick={onRemove}
          >
            {removeLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default CouponRemovePopup;
